import { addDays, format, setHours, setMilliseconds, setMinutes, setSeconds } from 'date-fns';
import { find, sortBy } from 'lodash';
import AbstractAuthenticatedService from '@vertical-plus/vue-js-micro-service-components/lib/model/service/abstract-authenticated-service';
import DeleteTimesheetTask from '@/model/task/audit/delete-timesheet-task';
import FormFieldInterface from '@vertical-plus/vue-js-components/lib/model/vos/form/form-field-interface';
import GetTimesheetsTask from '@/model/task/audit/get-timesheets-task';
import PatchTimesheetTask from '@/model/task/audit/patch-timesheet-task';
import PostTimesheetTask from '@/model/task/audit/post-timesheet-task';
import Timesheet from '@/model/entity/audit/timesheet';
import TimesheetSearchConditionsInterface from '@/model/vo/audit/timesheet-search-conditions-interface';
import TimesheetSearchResultsVo from '@/model/vo/audit/timesheet-search-results-vo';

export default class TimesheetService extends AbstractAuthenticatedService
{
    public static activityOptions: FormFieldInterface['options'] = [
        {
            value: 'communication',
            title: 'Communication',
        },
        {
            value: 'investigation',
            title: 'Investigation',
        },
        {
            value: 'planning',
            title: 'Planning',
        },
        {
            value: 'programming',
            title: 'Programming',
        },
        {
            value: 'qc',
            title: 'Quality Control',
        },
        {
            value: 'uat',
            title: 'UAT',
        },
        {
            value: 'n/a',
            title: 'Not Working',
        },
    ];

    public static chargeableOptions: FormFieldInterface['options'] = [
        {
            value: 'ecommerce',
            title: 'Ecommerce - General',
        },
        {
            value: 'ecommerce_adwords',
            title: 'Ecommerce - Adwords',
        },
        {
            value: 'yes',
            title: 'Paid',
        },
        {
            value: 'no',
            title: 'Unpaid',
        },
        {
            value: 'n/a',
            title: 'Not Working',
        },
    ];

    /**
     * Returns the activity for a type value
     *
     * @param value
     * @returns
     */

    public static getActivityTitle(value?: string): string
    {
        if (!value)
        {
            return '-';
        }

        const type = find(TimesheetService.activityOptions, option => option.value === value);
        if (!type)
        {
            return '-';
        }

        return type.title;
    }

    /**
     * Returns the chargeable for a type value
     *
     * @param value
     * @returns
     */

    public static getChargeableTitle(value?: string): string
    {
        if (!value)
        {
            return '-';
        }

        const type = find(TimesheetService.chargeableOptions, option => option.value === value);
        if (!type)
        {
            return '-';
        }

        return type.title;
    }


    /**
     * Returns matching timesheets
     *
     * @returns
     */

    public async findTimesheets(searchConditions?: TimesheetSearchConditionsInterface): Promise<TimesheetSearchResultsVo>
    {
        const task = new GetTimesheetsTask();
        task.token = this.token;
        task.searchConditions = searchConditions;

        return this.runTask(task) as Promise<TimesheetSearchResultsVo>;
    }

    /**
     * Creates a timesheet
     *
     * @param timesheet
     * @returns
     */

    public async createTimesheet(timesheet: Timesheet): Promise<Timesheet>
    {
        const task = new PostTimesheetTask();
        task.token = this.token;
        task.timesheet = timesheet;

        return this.runTask(task) as Promise<Timesheet>;
    }

    /**
     * Updates a timesheet
     *
     * @param timesheet
     * @returns
     */

    public async updateTimesheet(timesheet: Timesheet): Promise<Timesheet>
    {
        const task = new PatchTimesheetTask();
        task.token = this.token;
        task.timesheet = timesheet;

        return this.runTask(task) as Promise<Timesheet>;
    }

    /**
     * Deletes a timesheet
     *
     * @param timesheetId
     * @returns
     */

    public async deleteTimesheet(timesheetId: number): Promise<void>
    {
        const task = new DeleteTimesheetTask();
        task.token = this.token;
        task.timesheetId = timesheetId;

        return this.runTask(task) as Promise<void>;
    }

    /**
     * Returns the latest timesheet for a user and day
     *
     * @param userId
     * @param date
     * @returns
     */

    public async getLatestTimesheet(userId: string, date: Date): Promise<Timesheet|null>
    {
        const results = await this.findTimesheets({
            'user': userId,
            'dateFrom[after]': format(date, 'yyyy-MM-dd'),
            'dateFrom[strictly_before]': format(addDays(date, 1), 'yyyy-MM-dd'),
            'itemsPerPage': 1,
            'order': [
                {
                    key: 'dateTo',
                    descending: true,
                },
            ],
        });

        return results.results.length > 0 ? results.results[0] : null;
    }

    /**
     * Returns the next date from for the given day and current user
     *
     * @param date
     * @param timesheets
     */

    public async getNextDateFrom(date: Date, timesheets: Timesheet[] = []): Promise<Date>
    {
        // Ensure the timesheets are sorted by date DESC
        const sorted = sortBy(timesheets, [ 'dateTo' ]).reverse();

        if (sorted.length > 0 && sorted[0].dateTo)
        {
            return sorted[0].dateTo;
        }
        else
        {
            const now = new Date();
            const startOfDay = setHours(setMinutes(setSeconds(setMilliseconds(date, 0), 0), 0), 9);

            return now < startOfDay ? now : startOfDay;
        }
    }
}